import { Condition, RuleType } from "../../api/ruleengine";
import React, { useEffect, useRef, useState } from "react";

import { toReadableText } from "../utils/utils";
import RemoveCircleOutlineIcon from "@mui/icons-material/RemoveCircleOutline";
import AddCircleOutlineOutlinedIcon from "@mui/icons-material/AddCircleOutlineOutlined";
import Select from "@mui/material/Select";
import MenuItem from "@mui/material/MenuItem";
import IconButton from "@mui/material/IconButton";
import TextField from "@mui/material/TextField";
import Autocomplete from "@mui/material/Autocomplete";
import ListSubheader from "@mui/material/ListSubheader";

export function RuleCondition(
  props: Readonly<{
    onAdd?(condition: Condition): void;
    onRemove?(condition: Condition): void;
    onConditionChange(condition: Condition): void;
    selectedSubject?: string;
    conditionStatementsBySubject?: any;
    selectedStatement?: string;
    userInput?: any;
    additionalAttributeId?: number;
    disableRuleEdits?: boolean;
    ruleType: RuleType;
    conditionId?: number;
  }>
) {
  const {
    onAdd,
    onRemove,
    conditionStatementsBySubject,
    onConditionChange,
    disableRuleEdits,
    conditionId,
  } = props;

  const [selectedSubject, setSelectedSubject] = useState<string>(
    props.selectedSubject
  );

  const [selectedStatement, setSelectedStatement] = useState<string>(
    props.selectedStatement
  );
  const lastEmittedCondition = useRef<string>();

  const [statementsByCategory, setStatementsByCategory] = useState<any[]>([]);
  const [selectedStatementObj, setSelectedStatementObj] = useState<any>({});

  const [statementsForSubject, setStatementsForSubject] =
    useState<any>(undefined);

  const [userInput, setUserInput] = useState<any>(props.userInput || "");
  const [selectedAdditionalAttributeId, setSelectedAdditionalAttributeId] =
    useState<number | undefined>(props.additionalAttributeId);

  const [selectedEntity, setSelectedEntity] = useState<string>("ALL");
  const getDefaultAdditionalAttributeId = (subjectName: string) =>
    conditionStatementsBySubject?.[subjectName]?.attribute_options?.[0]?.id;

  const handleConditionSubjectChange = (event) => {
    setSelectedSubject(event.target.value);
    setSelectedStatement(
      conditionStatementsBySubject[event.target.value].statements[0].name
    );
    setSelectedAdditionalAttributeId(
      getDefaultAdditionalAttributeId(event.target.value)
    );
    setUserInput("");
  };

  useEffect(() => {
    if (disableRuleEdits) {
      return;
    }
    const nextCondition = {
      subject: selectedSubject,
      statement: selectedStatement,
      expectation: userInput,
      additional_attribute_id: selectedAdditionalAttributeId,
      id: conditionId,
    };
    const serializedCondition = JSON.stringify(nextCondition);
    if (lastEmittedCondition.current === serializedCondition) {
      return;
    }
    lastEmittedCondition.current = serializedCondition;
    onConditionChange(nextCondition);
  }, [
    conditionId,
    disableRuleEdits,
    selectedAdditionalAttributeId,
    selectedStatement,
    selectedSubject,
    userInput,
  ]);

  useEffect(() => {
    if (!selectedSubject) {
      if (
        conditionStatementsBySubject &&
        Object.entries(conditionStatementsBySubject).length > 0
      ) {
        setSelectedSubject(Object.entries(conditionStatementsBySubject)[0][0]);
        if (!selectedStatement) {
          setSelectedStatement(
            (Object.entries(conditionStatementsBySubject)[0][1] as any)
              .statements[0].name
          );
        }
        if (props.additionalAttributeId === undefined) {
          setSelectedAdditionalAttributeId(
            getDefaultAdditionalAttributeId(
              Object.entries(conditionStatementsBySubject)[0][0] as string
            )
          );
        }
      }
    }
  }, [
    conditionStatementsBySubject,
    props.additionalAttributeId,
    selectedStatement,
    selectedSubject,
  ]);

  useEffect(() => {
    if (selectedSubject) {
      setStatementsForSubject(conditionStatementsBySubject[selectedSubject]);
    }
  }, [selectedSubject, conditionStatementsBySubject]);

  useEffect(() => {
    const byCategory = {};
    Object.entries(conditionStatementsBySubject)
      .filter((statement) => {
        return (
          // @ts-ignore
          !statement[1].deprecated || statement[1].name === selectedSubject
        );
      })
      .forEach((statement) => {
        // @ts-ignore
        const category = statement[1].category;
        if (!byCategory[category]) {
          byCategory[category] = [];
        }
        byCategory[category].push(statement[0]);
      });
    // @ts-ignore
    setStatementsByCategory(byCategory);
  }, [conditionStatementsBySubject]);

  useEffect(() => {
    if (
      conditionStatementsBySubject &&
      selectedSubject &&
      selectedStatement &&
      conditionStatementsBySubject[selectedSubject]
    ) {
      const filtered = conditionStatementsBySubject[
        selectedSubject
      ].statements.filter((value) => value.name === selectedStatement);

      if (filtered.length > 0) {
        setSelectedStatementObj(filtered[0]);
      }
    }
  }, [conditionStatementsBySubject, selectedSubject, selectedStatement]);

  useEffect(() => {
    if (props.selectedSubject !== undefined) {
      setSelectedSubject(props.selectedSubject);
    }
  }, [props.selectedSubject]);

  useEffect(() => {
    if (props.selectedStatement !== undefined) {
      setSelectedStatement(props.selectedStatement);
    }
  }, [props.selectedStatement]);

  useEffect(() => {
    if (props.userInput !== undefined) {
      setUserInput(props.userInput || "");
    }
  }, [props.userInput]);

  useEffect(() => {
    setSelectedAdditionalAttributeId(props.additionalAttributeId);
  }, [props.additionalAttributeId]);

  useEffect(() => {
    const subjectMeta = selectedSubject
      ? conditionStatementsBySubject?.[selectedSubject]
      : undefined;
    if (subjectMeta === undefined) {
      return;
    }
    const attributeOptions = subjectMeta.attribute_options || [];
    if (attributeOptions.length === 0) {
      if (selectedAdditionalAttributeId !== undefined) {
        setSelectedAdditionalAttributeId(undefined);
      }
      return;
    }
    if (
      selectedAdditionalAttributeId === undefined &&
      props.additionalAttributeId !== undefined
    ) {
      return;
    }
    const selectedOption = attributeOptions.find(
      (option) => option.id === selectedAdditionalAttributeId
    );
    if (!selectedOption) {
      setSelectedAdditionalAttributeId(attributeOptions[0].id);
      setUserInput("");
    }
  }, [
    conditionStatementsBySubject,
    props.additionalAttributeId,
    selectedAdditionalAttributeId,
    selectedSubject,
  ]);

  const dataOptions: any[] = statementsForSubject?.data_options || [];
  const multipleChoiceOptions = dataOptions.map((option) =>
    typeof option === "string" ? { title: option, id: option } : option
  );
  const selectedIds =
    typeof userInput === "string" && userInput !== ""
      ? userInput.split("~")
      : [];
  const multipleChoiceValue = multipleChoiceOptions.filter((option) =>
    selectedIds.includes(option.id)
  );
  const singleChoiceValue = dataOptions.includes(userInput) ? userInput : null;

  return (
    <div
      style={{
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        marginBottom: "10px",
      }}
      data-testid={"rule-condition"}
    >
      {props.ruleType === RuleType.DUPLICATES && (
        <Select
          style={{ marginRight: "0.5em", flex: "1" }}
          variant={"standard"}
          value={selectedEntity}
          onChange={(event) => setSelectedEntity(event.target.value)}
        >
          <MenuItem value={"ALL"}>All Members</MenuItem>
          <MenuItem value={"PRIMARY"}>Primary</MenuItem>
          <MenuItem value={"SECONDARY"}>Secondaries</MenuItem>
        </Select>
      )}

      <Select
        variant={"standard"}
        value={selectedSubject || ""}
        onChange={handleConditionSubjectChange}
        style={{ marginRight: "0.5em", flex: "1" }}
        disabled={disableRuleEdits}
      >
        {Object.entries(statementsByCategory).map((categoryAndStatements) => {
          return [
            <ListSubheader key={categoryAndStatements[0]}>
              {categoryAndStatements[0]}
            </ListSubheader>,
            ...categoryAndStatements[1].map((item) => (
              <MenuItem key={`${item}.${item}`} value={item}>
                {toReadableText(item)}
              </MenuItem>
            )),
          ];
        })}
      </Select>
      {statementsForSubject?.attribute_options?.length > 0 && (
        <Select
          variant={"standard"}
          disabled={disableRuleEdits}
          value={selectedAdditionalAttributeId || ""}
          style={{ marginRight: "0.5em", flex: 1 }}
          onChange={(event) => {
            setSelectedAdditionalAttributeId(Number(event.target.value));
            setUserInput("");
          }}
        >
          {statementsForSubject.attribute_options.map((attributeOption) => (
            <MenuItem key={attributeOption.id} value={attributeOption.id}>
              {attributeOption.title}
              {attributeOption.deprecated ? " (Unavailable)" : ""}
            </MenuItem>
          ))}
        </Select>
      )}
      {statementsForSubject ? (
        <Select
          variant={"standard"}
          disabled={disableRuleEdits}
          id="conditions-statement-select"
          value={selectedStatement || ""}
          style={{ marginRight: "0.5em", flex: 1 }}
          onChange={(event) => {
            setSelectedStatement(event.target.value);
          }}
        >
          {statementsForSubject.statements.map((statement) => {
            return (
              <MenuItem key={statement.name} value={statement.name}>
                {statement.label || toReadableText(statement.name)}
              </MenuItem>
            );
          })}
        </Select>
      ) : null}

      {statementsForSubject?.data_options?.length > 0 &&
        selectedStatementObj?.field_type === "multiple_choice" &&
        selectedStatementObj.requires_input && (
          <Autocomplete
            disabled={disableRuleEdits}
            multiple
            limitTags={2}
            style={{ flex: 1 }}
            //@ts-ignore
            getOptionLabel={(value) => toReadableText(value.title)}
            options={multipleChoiceOptions}
            value={multipleChoiceValue}
            isOptionEqualToValue={(option, value) => option.id === value.id}
            onChange={(_event, value) => {
              // @ts-ignore
              setUserInput(value.map((object) => object.id).join("~"));
            }}
            renderInput={(params) => (
              <TextField
                {...params}
                variant="standard"
                margin={"dense"}
                size={"small"}
                disabled={disableRuleEdits}
              />
            )}
          />
        )}
      {statementsForSubject?.data_options?.length > 0
        ? selectedStatementObj?.field_type === "multiple_choice"
          ? null
          : selectedStatementObj?.requires_input && (
              <Autocomplete
                style={{ flex: 1 }}
                disabled={disableRuleEdits}
                options={statementsForSubject.data_options}
                getOptionLabel={(value) => toReadableText(value as string)}
                value={singleChoiceValue}
                onChange={(_event, value) => setUserInput(value ?? "")}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    variant="standard"
                    margin={"dense"}
                    size={"small"}
                  />
                )}
              />
            )
        : selectedStatementObj?.requires_input && (
            <TextField
              variant={"standard"}
              margin={"dense"}
              size={"small"}
              disabled={disableRuleEdits}
              onChange={(event) => {
                let val: any = event.target.value;
                if (selectedStatementObj?.field_type === "num" && val !== "") {
                  const parsedValue = Number.parseFloat(val);
                  val = Number.isNaN(parsedValue) ? "" : parsedValue;
                }

                setUserInput(val);
              }}
              value={userInput}
              style={{ flex: 1, margin: 0 }}
              type={
                selectedStatementObj?.field_type === "num" ? "number" : "text"
              }
            />
          )}

      <div style={{ display: "flex" }}>
        <IconButton
          data-testid={"remove_condition_btn"}
          aria-label="remove_condition"
          color="primary"
          size="small"
          disabled={!onRemove || disableRuleEdits}
          onClick={() => {
            onRemove({
              subject: selectedSubject,
              statement: selectedStatement,
              expectation: userInput,
              additional_attribute_id: selectedAdditionalAttributeId,
            });
          }}
        >
          <RemoveCircleOutlineIcon />
        </IconButton>
        <IconButton
          data-testid={"add_condition_btn"}
          color="primary"
          disabled={!onAdd || disableRuleEdits}
          size="small"
          onClick={() => {
            onAdd({
              subject: selectedSubject,
              statement: selectedStatement,
              expectation: userInput,
              additional_attribute_id: selectedAdditionalAttributeId,
            });
          }}
        >
          <AddCircleOutlineOutlinedIcon />
        </IconButton>
      </div>
    </div>
  );
}
